import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { StatusChip, PrioChip, Empty, errMsg, STATUS_LABEL } from "./TmParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const COLS = [["todo", "To Do"], ["in_progress", "In Progress"], ["review", "Review"], ["completed", "Completed"]];
const COL_TONE = { todo: "border-t-slate-400", in_progress: "border-t-primary", review: "border-t-purple-500", completed: "border-t-emerald-500" };

export function TmList({ data, onOpen, selected, setSelected, canBulk, page, setPage }) {
  if (!data) return null;
  if (!data.items.length) return <Empty text="No tasks match these filters." testid="tm-list-empty" />;
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  return (
    <div className="card-soft overflow-hidden" data-testid="tm-list">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>{canBulk && <th className="w-8 p-2" />}<th className="text-left p-2.5">Task</th><th className="text-left p-2.5 hidden md:table-cell">Department</th><th className="text-left p-2.5">Owner</th>
              <th className="text-left p-2.5">Priority</th><th className="text-left p-2.5">Status</th><th className="text-left p-2.5">Due</th><th className="text-left p-2.5 hidden lg:table-cell">Progress</th></tr>
          </thead>
          <tbody>
            {data.items.map((t) => (
              <tr key={t.task_id} className="border-t border-border hover:bg-accent/50 cursor-pointer" onClick={() => onOpen(t.task_id)} data-testid={`tm-row-${t.task_id}`}>
                {canBulk && <td className="p-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" className="accent-primary" checked={selected.includes(t.task_id)} onChange={() => toggle(t.task_id)} data-testid={`tm-select-${t.task_id}`} /></td>}
                <td className="p-2.5"><p className="font-medium leading-tight">{t.title}</p><p className="text-[11px] text-muted-foreground metric">{t.task_id}{t.project_id ? ` · ${t.project_id}` : ""}</p></td>
                <td className="p-2.5 hidden md:table-cell text-xs text-muted-foreground">{t.department}</td>
                <td className="p-2.5 text-xs">{t.assigned_to?.name}</td>
                <td className="p-2.5"><PrioChip p={t.priority} /></td>
                <td className="p-2.5"><StatusChip s={t.status} /></td>
                <td className={`p-2.5 text-xs metric ${t.overdue ? "text-signal-red font-bold" : ""}`}>{t.due_date || "—"}{t.overdue ? " · overdue" : ""}</td>
                <td className="p-2.5 hidden lg:table-cell w-32">
                  {t.checklist_progress?.total ? <div className="h-1.5 rounded bg-muted"><div className="h-1.5 rounded bg-primary" style={{ width: `${Math.round(t.checklist_progress.done * 100 / t.checklist_progress.total)}%` }} /></div> : <span className="text-[11px] text-muted-foreground">—</span>}
                </td>
              </tr>))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between p-2.5 text-xs text-muted-foreground border-t border-border">
        <span>{data.total} tasks · page {data.page}/{pages}</span>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="tm-prev">Prev</Button>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="tm-next">Next</Button>
        </div>
      </div>
    </div>);
}

export function TmKanban({ cols, onOpen, reload }) {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const drop = async (column) => {
    setOver(null);
    if (!drag) return;
    try {
      let reason;
      if (column === "todo") { reason = window.prompt("Reason for returning this work (required):"); if (!reason) return; }
      await api.post(`/tm/tasks/${drag}/move`, { column, reason });
      toast.success("Task moved");
      reload();
    } catch (e) { toast.error(errMsg(e, "Move not allowed")); }
    setDrag(null);
  };
  if (!cols) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="tm-kanban">
      {COLS.map(([key, label]) => (
        <div key={key} className={`card-soft border-t-4 ${COL_TONE[key]} p-2 min-h-[300px] ${over === key ? "ring-2 ring-primary/40" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setOver(key); }} onDragLeave={() => setOver(null)} onDrop={() => drop(key)} data-testid={`kanban-col-${key}`}>
          <div className="flex items-center justify-between px-1 pb-2"><p className="text-sm font-semibold">{label}</p><span className="tm-chip tm-muted">{cols[key]?.length || 0}</span></div>
          <div className="space-y-2">
            {(cols[key] || []).map((t) => (
              <div key={t.task_id} draggable onDragStart={() => setDrag(t.task_id)} onClick={() => onOpen(t.task_id)}
                className="rounded-lg border border-border bg-card p-2.5 cursor-grab hover:shadow-md transition-shadow" data-testid={`kanban-card-${t.task_id}`}>
                <p className="text-sm font-medium leading-tight">{t.title}</p>
                <div className="flex flex-wrap items-center gap-1 mt-1.5"><span className="tm-chip tm-muted">{t.department}</span><PrioChip p={t.priority} />{t.status !== key && STATUS_LABEL[t.status] !== label && <StatusChip s={t.status} />}</div>
                <p className={`text-[11px] mt-1.5 metric ${t.overdue ? "text-signal-red font-semibold" : "text-muted-foreground"}`}>{t.assigned_to?.name} · {t.due_date || "no due"}</p>
              </div>))}
          </div>
        </div>))}
    </div>);
}

export function TmCalendar({ data, month, setMonth, onOpen }) {
  const grid = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const cells = [];
    for (let i = 0; i < first.getDay(); i++) cells.push(null);
    const days = new Date(y, m, 0).getDate();
    for (let d = 1; d <= days; d++) cells.push(`${month}-${String(d).padStart(2, "0")}`);
    return cells;
  }, [month]);
  const shift = (n) => { const [y, m] = month.split("-").map(Number); const d = new Date(y, m - 1 + n, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); };
  const today = new Date().toISOString().slice(0, 10);
  const ms = {};
  (data?.milestones || []).forEach((p) => { (ms[p.deadline] = ms[p.deadline] || []).push(p); });
  return (
    <div className="card-soft p-3" data-testid="tm-calendar">
      <div className="flex items-center justify-between mb-2">
        <Button size="sm" variant="outline" onClick={() => shift(-1)} data-testid="cal-prev">‹</Button>
        <p className="font-semibold">{new Date(`${month}-01`).toLocaleString("en-IN", { month: "long", year: "numeric" })}</p>
        <Button size="sm" variant="outline" onClick={() => shift(1)} data-testid="cal-next">›</Button>
      </div>
      <div className="grid grid-cols-7 text-[11px] text-center text-muted-foreground mb-1">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((d, i) => (
          <div key={d || `e${i}`} className={`min-h-[72px] rounded-md border p-1 ${d ? "border-border bg-background" : "border-transparent"} ${d === today ? "ring-2 ring-primary/50" : ""}`}>
            {d && <p className="text-[11px] font-semibold text-muted-foreground">{Number(d.slice(-2))}</p>}
            {d && (ms[d] || []).map((p) => <p key={p.project_id} className="text-[10px] truncate rounded px-1 bg-purple-100 text-purple-700">◆ {p.name}</p>)}
            {d && (data?.days?.[d] || []).slice(0, 3).map((t) => (
              <p key={t.task_id} onClick={() => onOpen(t.task_id)} className={`text-[10px] truncate rounded px-1 cursor-pointer mt-0.5 ${t.overdue ? "bg-red-100 text-red-700" : t.status === "verified" || t.status === "closed" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`} data-testid={`cal-task-${t.task_id}`}>{t.title}</p>))}
            {d && (data?.days?.[d] || []).length > 3 && <p className="text-[10px] text-muted-foreground">+{data.days[d].length - 3} more</p>}
          </div>))}
      </div>
    </div>);
}

export function TmTimeline({ rows, onOpen }) {
  const { min, span } = useMemo(() => {
    if (!rows?.length) return { min: null, span: 1 };
    const ds = rows.flatMap((r) => [r.start, r.end]).map((d) => new Date(d).getTime());
    const mn = Math.min(...ds), mx = Math.max(...ds);
    return { min: mn, span: Math.max(1, (mx - mn) / 86400000 + 1) };
  }, [rows]);
  if (!rows) return null;
  if (!rows.length) return <Empty text="No scheduled tasks (set start/due dates to see them here)." testid="tm-timeline-empty" />;
  const pos = (d) => ((new Date(d).getTime() - min) / 86400000 / span) * 100;
  const todayX = pos(new Date().toISOString().slice(0, 10));
  return (
    <div className="card-soft p-3 overflow-x-auto" data-testid="tm-timeline">
      <div className="min-w-[700px] relative">
        {todayX >= 0 && todayX <= 100 && <div className="absolute top-0 bottom-0 w-px bg-signal-red/70 z-10" style={{ left: `calc(220px + (100% - 220px) * ${todayX / 100})` }} title="Today" />}
        {rows.map((r) => (
          <div key={r.task_id} className="flex items-center gap-2 py-1 border-b border-border/60 last:border-0">
            <div className="w-[220px] shrink-0 truncate text-xs cursor-pointer hover:text-primary" onClick={() => onOpen(r.task_id)} data-testid={`gantt-row-${r.task_id}`}>
              <span className="font-medium">{r.title}</span><span className="text-muted-foreground"> · {r.assignee}</span>
              {r.blocked_by?.length > 0 && <span className="text-[10px] text-muted-foreground"> ⇠ {r.blocked_by.join(", ")}</span>}
            </div>
            <div className="flex-1 relative h-6">
              <div className={`absolute h-5 top-0.5 rounded-md text-[10px] text-white px-1.5 leading-5 truncate ${r.overdue ? "bg-signal-red" : r.status === "verified" || r.status === "closed" ? "bg-emerald-500" : r.status === "blocked" ? "bg-amber-500" : "bg-primary"}`}
                style={{ left: `${pos(r.start)}%`, width: `${Math.max(2, pos(r.end) - pos(r.start) + 100 / span)}%` }}>{r.start} → {r.end}</div>
            </div>
          </div>))}
      </div>
    </div>);
}

export function BulkBar({ selected, clear, users, reload }) {
  const [val, setVal] = useState("");
  const run = async (action, value) => {
    try {
      const r = await api.post("/tm/tasks/bulk", { task_ids: selected, action, value, reason: val || null });
      toast.success(`${r.data.done.length} updated${r.data.errors.length ? `, ${r.data.errors.length} failed` : ""}`);
      clear(); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!selected.length) return null;
  return (
    <div className="card-soft p-2.5 flex flex-wrap items-center gap-2 text-xs" data-testid="tm-bulk-bar">
      <span className="font-semibold">{selected.length} selected</span>
      {["critical", "high", "normal", "low"].map((p) => <Button key={p} size="sm" variant="outline" className="capitalize" onClick={() => run("priority", p)} data-testid={`bulk-prio-${p}`}>{p}</Button>)}
      <select className="h-8 rounded-md border border-input bg-background px-2" onChange={(e) => e.target.value && run("assign", e.target.value)} defaultValue="" data-testid="bulk-assign">
        <option value="">Reassign to…</option>{users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
      </select>
      <Input className="h-8 w-44" placeholder="Reason (cancel/archive)" value={val} onChange={(e) => setVal(e.target.value)} data-testid="bulk-reason" />
      <Button size="sm" variant="outline" className="text-signal-red" disabled={!val} onClick={() => run("cancel")} data-testid="bulk-cancel">Cancel tasks</Button>
      <Button size="sm" variant="ghost" onClick={clear}>Clear</Button>
    </div>);
}
